import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import SubActivityView from '../sub-activity/sub-activity-view';
import ActivitySheet from '../shared/sheet/activity-sheet';
import { ClassroomAnswerInstruction } from '../../classroom/classroom-answer.style';
import ClassroomAnswerTabs, {
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import { ComponentResponse } from '../component.constants';
import { ResponseViewContainer } from './response-view.style';
import ResponseViewField from './response-view-field';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useResponseBaseHeight } from './response.hooks';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import useResponseHasSuggestedAnswer from './use-response-has-suggested-answer';
import { useBatchAnswersContext } from '../shared/batch-answers.context';
import TextResponseIndividual from 'approot/activity-builder/classroom/text-response/text-response-individual';
import TextResponseSuggestedAnswers from 'approot/activity-builder/classroom/text-response/text-response-suggested-answers';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';

type Props = Omit<ComponentResponse, '__typename'> & {
  isPublished: boolean;
  onClose: () => void;
};

const ResponseCheckAnswerTeacher = (props: Props) => {
  const {
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    id,
    type,
    isPublished,
    onClose,
  } = props;

  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const baseHeight = useResponseBaseHeight();

  const sortedFields = useMemo(
    () => sortBy(properties.textResponse.fields, 'sort'),
    [properties.textResponse.fields]
  );

  const { componentAnswers, variables } = useTeacherGetAnswers({
    fetchPolicy: 'cache-only',
    componentId: id,
    skip: !activityBuilderRespondFlag,
  });

  const { handleUpdate } = useBatchAnswersContext();
  const { tab, setTab } = useInitialClassroomTab({
    isVisible: true,
    canRespond: true,
    componentId: id,
  });

  const hasSuggestedAnswer = useResponseHasSuggestedAnswer({
    suggestedAnswerProperties,
  });

  return (
    <ActivitySheet
      isVisible
      title="Resonse component"
      hasBackgroundColor={tab === QUESTION_VIEW}
      customActions={
        <ActivitySheetSaveStatus
          title="Response component"
          showStatus={tab === QUESTION_VIEW}
          onClose={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
      tabsContent={
        <ClassroomAnswerTabs
          isPublished={isPublished}
          includeIndividualAnswers
          includeSuggestedAnswers={hasSuggestedAnswer}
          includeClassResponse={false}
          tab={tab}
          onSelect={option => setTab(option)}
          componentId={id}
          onCloseActivitySheet={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
    >
      <ActivitySheetInnerContent id="content">
        {tab === QUESTION_VIEW &&
          subActivityProperties?.activityComponentId && (
            <ActivitySheetInnerMax>
              <ClassroomAnswerInstruction>
                <SubActivityView
                  withTTSButton
                  componentId={id}
                  componentType={type}
                  subActivityDetails={subActivityProperties}
                >
                  <ResponseViewContainer>
                    {sortedFields.map(field => {
                      const answer = componentAnswers?.find(
                        a => a.answerReferences?.id === field.itemId
                      );
                      return (
                        <ResponseViewField
                          key={field.itemId}
                          updateAnswer={input => {
                            if (variables) {
                              handleUpdate({
                                input,
                                variables,
                              });
                            }
                          }}
                          answer={answer}
                          baseHeight={baseHeight}
                          field={field}
                          componentId={id}
                          activityId={
                            subActivityProperties?.activityComponentId
                          }
                          ariaLabelledBy={`subactivity-${id}-label`}
                        />
                      );
                    })}
                  </ResponseViewContainer>
                </SubActivityView>
              </ClassroomAnswerInstruction>
            </ActivitySheetInnerMax>
          )}
        {tab === INDIVIDUAL_VIEW && (
          <ActivitySheetGridMax>
            <TextResponseIndividual component={props} />
          </ActivitySheetGridMax>
        )}
        {tab === SUGGESTED_VIEW && (
          <ActivitySheetInnerMax>
            <TextResponseSuggestedAnswers component={props} />
          </ActivitySheetInnerMax>
        )}
      </ActivitySheetInnerContent>
    </ActivitySheet>
  );
};

export default ResponseCheckAnswerTeacher;
